import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Card, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ContentCopy, Email, Visibility } from '@mui/icons-material';
import { getInfluencers } from '../services/api';
import { toast } from 'react-toastify';

function IYData({ user }) {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const navigate = useNavigate();

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const fetchInfluencers = async () => {
    setLoading(true);
    try {
      const response = await getInfluencers();
      setInfluencers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = (rowId) => {
    const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0/${rowId}`;
    navigator.clipboard.writeText(formUrl);
    toast.success('Form URL copied to clipboard!');
  };

  const handleSendEmail = async (rowId) => {
    try {
      const inf = influencers.find(i => i._id === rowId);
      if (!inf?.email) {
        toast.error('No email found for this influencer');
        return;
      }
      const { default: api } = await import('../services/api');
      const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0/${rowId}`;
      await api.post('/send-form-email', { email: inf.email, formUrl });
      toast.success('Email sent successfully!');
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const getFilteredData = () => {
    let filtered = influencers;

    // Auth-based: non-admin sees only their onboarded influencers
    if (user?.role !== 'admin') {
      filtered = filtered.filter(inf => inf.onboarderId?._id === user?.id);
    }

    if (activeTab === 'pending') {
      return filtered.filter(inf => inf.ocr_status === 'pending');
    }
    return filtered.filter(inf => inf.ocr_status && inf.ocr_status === 'completed');
  };

  const columns = [
    { field: 'sno', headerName: 'SNO', width: 80 },
    { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
    { field: 'onboarderName', headerName: 'Onboard Name', width: 150 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'totalcostinstagram', headerName: 'Total Cost Instagram', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const value = params.row?.reelCost;
        if (!value || isNaN(value)) return '-';
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: 'AverageViewInstagram', headerName: 'Average view (Instagram)', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const totalViews = params.row?.allData?.last10ReelsViews;
        if (!totalViews || isNaN(totalViews)) return '-';
        return Number(totalViews) / 10;
      }
    },
    {
      field: 'AverageReachInstagram', headerName: 'Average reach (Instagram)', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const totalReach = params.row?.allData?.last10ReelsReach;
        if (!totalReach || isNaN(totalReach)) return '-';
        return Number(totalReach) / 10;
      }
    },
    {
      field: 'engagementRateInstagram', headerName: 'Engagement Rate (Instagram)', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const likes = Number(params.row?.allData?.instagramLikes) || 0;
        const comments = Number(params.row?.allData?.instagramComments) || 0;
        const saves = Number(params.row?.allData?.instagramSaves) || 0;
        const shares = Number(params.row?.allData?.instagramShares) || 0;
        const reposts = Number(params.row?.allData?.instagramReposts) || 0;
        const totalReach = Number(params.row?.allData?.last10ReelsReach);
        if (!totalReach || isNaN(totalReach)) return '-';
        const avgReach = totalReach / 10;
        if (avgReach === 0) return '-';
        const rate = ((likes + comments + saves + shares + reposts) / avgReach) * 100;
        return Number(rate.toFixed(2));
      }
    },
    {
      field: 'totalcostyoutube', headerName: 'Total Cost Youtube', flex: 1, minWidth: 200,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const shorts = params.row?.youtubeShortsCost;
        const video = params.row?.youtubeVideoCost;
        if (!shorts || !video) return '-';
        return `₹${(shorts + video).toLocaleString()}`;
      }
    },
    {
      field: 'AverageViewYoutube', headerName: 'Average view (YouTube)', flex: 1, minWidth: 200,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const totalViews = params.row?.allData?.last10ShortsViews;
        if (!totalViews || isNaN(totalViews)) return '-';
        return Number(totalViews) / 10;
      }
    },
    {
      field: 'AverageReachYoutube', headerName: 'Average Reach (YouTube)', flex: 1, minWidth: 200,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const totalReach = params.row?.allData?.last10ShortsReach;
        if (!totalReach || isNaN(totalReach)) return '-';
        return Number(totalReach) / 10;
      }
    },
    {
      field: 'engagementRateYoutube', headerName: 'Engagement Rate (Youtube)', flex: 1, minWidth: 230,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const likes = Number(params.row?.allData?.youtubeLikes) || 0;
        const comments = Number(params.row?.allData?.youtubeComments) || 0;
        const saves = Number(params.row?.allData?.youtubeSaves) || 0;
        const shares = Number(params.row?.allData?.youtubeShares) || 0;
        const totalReach = Number(params.row?.allData?.last10ShortsReach);
        if (!totalReach || isNaN(totalReach)) return '-';
        const avgReach = totalReach / 10;
        if (avgReach === 0) return '-';
        const rate = ((likes + comments + saves + shares) / avgReach) * 100;
        return Number(rate.toFixed(2));
      }
    },
    {
      field: 'createdAt', headerName: 'Created At', width: 200,
      valueGetter: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const h = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const hours = h % 12 || 12;
        const ampm = h >= 12 ? 'PM' : 'AM';
        return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
      }
    },
    {
      field: 'actions', headerName: 'Actions', width: 250,
      renderCell: (params) => {
        if (activeTab === 'completed') {
          return (
            <Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/influencer/${params.row._id}`)}>
              View
            </Button>
          );
        }
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<ContentCopy />} onClick={() => handleCopyUrl(params.row.id)}>
              Copy
            </Button>
            <Button size="small" startIcon={<Email />} onClick={() => handleSendEmail(params.row.id)}>
              Email
            </Button>
            <Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/influencer/${params.row._id}`)}>
              View
            </Button>
          </Box>
        );
      }
    }
  ];

  const filteredData = getFilteredData();

  const rows = filteredData.map((inf, index) => ({
    id: inf._id,
    sno: index + 1,
    influencerCode: inf.influencerCode,
    name: inf.name,
    email: inf.email,
    onboarderName: inf.onboarderId?.name || 'N/A',
    reelCost: inf.reelCost,
    youtubeVideoCost: inf.youtubeVideoCost,
    youtubeShortsCost: inf.youtubeShortsCost,
    createdAt: inf.createdAt,
    _id: inf._id,
    allData: inf,
  }));

  const pendingCount = (() => {
    let filtered = influencers;
    if (user?.role !== 'admin') filtered = filtered.filter(inf => inf.onboarderId?._id === user?.id);
    return filtered.filter(inf => inf.ocr_status === 'pending').length;
  })();

  const completedCount = (() => {
    let filtered = influencers;
    if (user?.role !== 'admin') filtered = filtered.filter(inf => inf.onboarderId?._id === user?.id);
    return filtered.filter(inf => inf.ocr_status && inf.ocr_status === 'completed').length;
  })();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        I & Y Data
      </Typography>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Pending (${pendingCount})`} value="pending" />
        <Tab label={`Completed (${completedCount})`} value="completed" />
      </Tabs>

      <Card sx={{ p: 2, boxShadow: 2 }}>
        <DataGrid
          columns={columns}
          rows={loading ? [] : rows}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          className="table_border"
          sx={{
            backgroundColor: 'white',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB' },
            '& .MuiDataGrid-iconSeparator': { display: 'none' },
            '& .MuiDataGrid-row': {
              backgroundColor: 'white',
              borderLeft: 'solid 1px #e5e7eb',
              borderRight: 'solid 1px #e5e7eb',
            },
            '& .instagram-cell': { backgroundColor: '#e8f4fcb7 !important' },
            '& .youtube-cell': { backgroundColor: '#fff5e69a !important' },
          }}
        />
      </Card>
    </Box>
  );
}

export default IYData;
